import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import i18n from '@/i18n';
import {
  type AttributeDef,
  type CredentialTypeSchemaResponse,
} from '@/api/credentialSchemas';
import type { CredentialType, MetaValue } from '@/api/credentials';
import { expiryInputFromIso } from '@/features/vault/ExpiryField';
import HibpCheck from '@/features/vault/HibpCheck';
import {
  PasswordGeneratorButton,
  PasswordStrength,
} from '@/features/vault/PasswordTools';
import {
  firstCertificate,
  inspectPem,
  pemExpectationFor,
  pemWarning,
  type PemExpectation,
  type PemWarning,
  type X509Info,
} from '@/features/vault/pem';
import { formatDate } from '@/lib/format';

export interface FormValues {
  /** Map attribute.key → string representation (number/date ca string, UI). */
  fields: Record<string, string>;
}

export interface PartitionedPayload {
  /** Atributele secret — vor intra în ciphertext. */
  plaintext: Record<string, string>;
  /** Atributele non-secret — vor intra în meta (server-visible). */
  meta: Record<string, MetaValue>;
}

/** Limită plaintext pentru atribute type=file. ~750 KB se mapează la
 *  ~1 MB base64 → ~1 MB ciphertext + overhead, sub MAX_BLOB_LEN 2 MB. */
export const FILE_MAX_PLAINTEXT_BYTES = 1024 * 1024;

/**
 * Separă valorile form-ului în plaintext-ul de criptat și meta-ul
 * server-visible, pe baza schemei. Aruncă Error dacă un required e gol.
 */
export function partitionValues(
  schema: CredentialTypeSchemaResponse,
  values: Record<string, string>,
): PartitionedPayload {
  const plaintext: Record<string, string> = {};
  const meta: Record<string, MetaValue> = {};
  for (const attr of schema.attributes) {
    const raw = values[attr.key] ?? '';
    if (attr.required && !raw.trim()) {
      throw new Error(i18n.t('vault_ui.form.required_field', { label: attr.label }));
    }
    if (!raw.trim()) continue;
    if (attr.secret) {
      plaintext[attr.key] = raw;
    } else if (attr.type === 'number') {
      const n = Number(raw);
      if (Number.isNaN(n)) {
        throw new Error(i18n.t('vault_ui.form.invalid_number', { label: attr.label }));
      }
      meta[attr.key] = n;
    } else {
      meta[attr.key] = raw;
    }
  }
  return { plaintext, meta };
}

// ==========================================================
// CredentialFormFields — coleția de inputs dinamice
// ==========================================================

interface FieldsProps {
  schema: CredentialTypeSchemaResponse | undefined;
  values: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  disabled: boolean;
  /** Data de expirare din formular (`YYYY-MM-DD` sau ''), pentru propunerea din certificat. */
  expires?: string;
  /** Primește data `notAfter` a certificatului lipit, ca `YYYY-MM-DD`. */
  onSuggestExpiry?: (value: string) => void;
}

export function CredentialFormFields({
  schema,
  values,
  onChange,
  disabled,
  expires,
  onSuggestExpiry,
}: FieldsProps) {
  const { t } = useTranslation();
  if (!schema) {
    return <p className="text-xs text-[var(--text-dim)]">{t('vault_ui.form.loading_schema')}</p>;
  }
  return (
    <div className="space-y-3">
      {schema.attributes.map((attr) => (
        <DynamicField
          key={attr.key}
          attr={attr}
          value={values[attr.key] ?? ''}
          values={values}
          onChange={(v) => onChange({ ...values, [attr.key]: v })}
          onSetMultiple={(patch) => onChange({ ...values, ...patch })}
          disabled={disabled}
          pemExpectation={pemExpectationFor(schema.type, attr)}
          expires={expires}
          onSuggestExpiry={onSuggestExpiry}
        />
      ))}
    </div>
  );
}

// ==========================================================
// Initializer — pre-populare din schema defaults și valori existente
// ==========================================================

export function initialFieldsFromSchema(
  schema: CredentialTypeSchemaResponse | undefined,
  existingMeta: Record<string, MetaValue> = {},
  existingPlaintext: Record<string, unknown> = {},
): Record<string, string> {
  if (!schema) return {};
  const next: Record<string, string> = {};
  for (const attr of schema.attributes) {
    if (attr.secret) {
      const v = existingPlaintext[attr.key];
      next[attr.key] = v === undefined || v === null ? '' : String(v);
    } else {
      const v = existingMeta[attr.key];
      next[attr.key] = v === undefined || v === null ? (attr.default ?? '') : String(v);
    }
  }
  return next;
}

// ==========================================================
// TypeSelector — helper pentru componentele externe
// ==========================================================

// `labelKey` e cheie i18n — se traduce la randare (`t(labelKey)`), nu la
// încărcarea modulului, ca eticheta să urmeze limba curentă.
export const CRED_TYPES: Array<{ value: CredentialType; labelKey: string }> = [
  { value: 'password', labelKey: 'vault_ui.form.types.password' },
  { value: 'api_key', labelKey: 'vault_ui.form.types.api_key' },
  { value: 'ssh_key', labelKey: 'vault_ui.form.types.ssh_key' },
  { value: 'certificate', labelKey: 'vault_ui.form.types.certificate' },
  { value: 'totp_seed', labelKey: 'vault_ui.form.types.totp_seed' },
  { value: 'db_connection', labelKey: 'vault_ui.form.types.db_connection' },
  { value: 'secure_file', labelKey: 'vault_ui.form.types.secure_file' },
  { value: 'generic', labelKey: 'vault_ui.form.types.generic' },
];

// ==========================================================
// Hook pentru re-sync valori la schimb de tip (doar în create mode)
// ==========================================================

export function useResetFieldsOnSchemaChange(
  schema: CredentialTypeSchemaResponse | undefined,
  setValues: (v: Record<string, string>) => void,
  enabled: boolean,
) {
  useEffect(() => {
    if (!enabled || !schema) return;
    setValues(initialFieldsFromSchema(schema));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema?.type, schema?.attributes, enabled]);
}

// ==========================================================
// Câmp individual (internal)
// ==========================================================

interface DynamicFieldProps {
  attr: AttributeDef;
  value: string;
  values: Record<string, string>;
  onChange: (v: string) => void;
  onSetMultiple: (patch: Record<string, string>) => void;
  disabled: boolean;
  /** `null` = câmp obișnuit, fără verificare PEM. */
  pemExpectation: PemExpectation | null;
  expires?: string;
  onSuggestExpiry?: (value: string) => void;
}

function DynamicField({
  attr,
  value,
  values,
  onChange,
  onSetMultiple,
  disabled,
  pemExpectation,
  expires,
  onSuggestExpiry,
}: DynamicFieldProps) {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState(false);
  // Câmpurile se generează din schemă, deci id-ul trebuie să fie unic per instanță.
  const fieldId = useId();
  const label = (
    <label htmlFor={fieldId} className="block text-xs font-medium text-[var(--text)] mb-1">
      {attr.label}
      {attr.required && <span className="text-red-500 ml-0.5">*</span>}
      {attr.secret && (
        <span className="ml-2 text-[10px] uppercase text-brand-700 font-semibold">
          {t('vault_ui.form.e2e_badge')}
        </span>
      )}
    </label>
  );

  const common = {
    id: fieldId,
    className: attr.secret ? 'input font-mono' : 'input',
    value,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange(e.target.value),
    required: attr.required,
    disabled,
    placeholder: attr.default ?? undefined,
  };

  if (attr.type === 'textarea' && pemExpectation) {
    return (
      <PemField
        attr={attr}
        value={value}
        onChange={onChange}
        disabled={disabled}
        label={label}
        inputId={fieldId}
        expectation={pemExpectation}
        expires={expires}
        onSuggestExpiry={onSuggestExpiry}
      />
    );
  }
  if (attr.type === 'textarea') {
    return (
      <div>
        {label}
        <textarea {...common} rows={3} className={common.className + ' resize-y'} />
      </div>
    );
  }
  if (attr.type === 'secret') {
    return (
      <div>
        <div className="flex items-center justify-between">
          {label}
          <div className="flex items-center gap-3 mb-1">
            <PasswordGeneratorButton onGenerate={(pwd) => onChange(pwd)} />
            <button
              type="button"
              className="text-[10px] text-[var(--text-dim)] hover:text-brand-600"
              onClick={() => setRevealed((v) => !v)}
            >
              {revealed ? t('vault.hide') : t('vault_ui.form.show')}
            </button>
          </div>
        </div>
        <input
          {...common}
          type={revealed ? 'text' : 'password'}
          autoComplete="off"
        />
        <PasswordStrength password={value} />
        <HibpCheck password={value} />
      </div>
    );
  }
  if (attr.type === 'number') {
    return (
      <div>
        {label}
        <input {...common} type="number" />
      </div>
    );
  }
  if (attr.type === 'date') {
    return (
      <div>
        {label}
        <input {...common} type="date" />
      </div>
    );
  }
  if (attr.type === 'file') {
    return (
      <FileField
        attr={attr}
        value={value}
        currentFilename={values.filename ?? ''}
        disabled={disabled}
        label={label}
        inputId={fieldId}
        onSetMultiple={onSetMultiple}
      />
    );
  }
  return (
    <div>
      {label}
      <input
        {...common}
        type={attr.type === 'email' ? 'email' : attr.type === 'url' ? 'url' : 'text'}
      />
    </div>
  );
}

// ==========================================================
// FileField — input type file cu auto-populate `filename` (dacă schema
// are acel atribut) și limită hard 1 MB plaintext.
// ==========================================================

function FileField({
  attr,
  value,
  currentFilename,
  disabled,
  label,
  inputId,
  onSetMultiple,
}: {
  attr: AttributeDef;
  value: string;
  currentFilename: string;
  disabled: boolean;
  label: React.ReactNode;
  inputId: string;
  onSetMultiple: (patch: Record<string, string>) => void;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const hasFile = value.length > 0;
  const approxBytes = hasFile ? Math.ceil((value.length * 3) / 4) : 0;

  const handleFile = async (file: File) => {
    setError(null);
    if (file.size > FILE_MAX_PLAINTEXT_BYTES) {
      setError(
        t('vault_ui.form.file_too_large', {
          size: formatBytes(file.size),
          limit: formatBytes(FILE_MAX_PLAINTEXT_BYTES),
        }),
      );
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setBusy(true);
    try {
      const buf = await file.arrayBuffer();
      const b64 = arrayBufferToBase64(buf);
      const patch: Record<string, string> = { [attr.key]: b64 };
      // Auto-populate `filename` dacă user nu a editat manual
      if (!currentFilename || currentFilename === '—') {
        patch['filename'] = file.name;
      }
      onSetMultiple(patch);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('vault_ui.form.read_error'));
    } finally {
      setBusy(false);
    }
  };

  const clearFile = () => {
    setError(null);
    onSetMultiple({ [attr.key]: '' });
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div>
      {label}
      <div className="flex items-center gap-2">
        <input
          id={inputId}
          ref={fileRef}
          type="file"
          className="input text-xs file:mr-3 file:rounded file:border-0 file:bg-[var(--bg-surface)] file:px-3 file:py-1 file:text-xs file:cursor-pointer"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
          }}
          disabled={disabled || busy}
          required={attr.required && !hasFile}
        />
        {hasFile && (
          <button
            type="button"
            className="text-xs hover:underline"
            style={{ color: 'var(--danger)' }}
            onClick={clearFile}
            disabled={disabled || busy}
          >
            {t('vault_ui.form.remove_file')}
          </button>
        )}
      </div>
      {hasFile && (
        <div
          className="text-[11px] mt-1 font-mono"
          style={{ color: 'var(--text-dim)' }}
        >
          {t('vault_ui.form.file_info', {
            name: currentFilename || '—',
            size: formatBytes(approxBytes),
          })}
        </div>
      )}
      <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-dim)' }}>
        {t('vault_ui.form.file_hint', { limit: formatBytes(FILE_MAX_PLAINTEXT_BYTES) })}
      </div>
      {error && (
        <div className="text-xs mt-1" style={{ color: 'var(--danger)' }}>
          {error}
        </div>
      )}
    </div>
  );
}

// ==========================================================
// PemField — zonă de text pentru chei și certificate: avertisment discret
// dacă formatul nu e recunoscut (salvarea NU e blocată) și, pentru un
// certificat, propunerea datei de expirare din `notAfter`.
// ==========================================================

const PEM_WARNING_KEYS: Record<PemWarning, string> = {
  no_pem: 'pem.warning.no_pem',
  missing_begin: 'pem.warning.missing_begin',
  missing_end: 'pem.warning.missing_end',
  label_mismatch: 'pem.warning.label_mismatch',
  unknown_label: 'pem.warning.unknown_label',
  empty_body: 'pem.warning.empty_body',
  bad_base64: 'pem.warning.bad_base64',
  bad_structure: 'pem.warning.bad_structure',
  bad_certificate: 'pem.warning.bad_certificate',
  bad_ssh_public: 'pem.warning.bad_ssh_public',
  expected_certificate: 'pem.warning.expected_certificate',
  expected_private_key: 'pem.warning.expected_private_key',
  expected_key: 'pem.warning.expected_key',
};

/**
 * `notAfter` ca valoare pentru ExpiryField (ziua în fusul local). `null` pentru
 * 9999-12-31, prin care RFC 5280 marchează „fără dată de expirare".
 */
function expiryFromCertificate(cert: X509Info | null): string | null {
  if (!cert || cert.notAfter.getUTCFullYear() >= 9999) return null;
  return expiryInputFromIso(cert.notAfter.toISOString()) || null;
}

function PemField({
  attr,
  value,
  onChange,
  disabled,
  label,
  inputId,
  expectation,
  expires,
  onSuggestExpiry,
}: {
  attr: AttributeDef;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  label: React.ReactNode;
  inputId: string;
  expectation: PemExpectation;
  expires?: string;
  onSuggestExpiry?: (value: string) => void;
}) {
  const { t } = useTranslation();
  const [fileError, setFileError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const inspection = useMemo(() => (value.trim() ? inspectPem(value) : null), [value]);
  const warning = inspection ? pemWarning(inspection, expectation) : null;
  const certificate = inspection ? firstCertificate(inspection) : null;
  const suggested = expiryFromCertificate(certificate);

  const apply = (next: string) => {
    onChange(next);
    if (!onSuggestExpiry || expires) return;
    // Completăm singuri doar când certificatul lipit aduce o dată nouă — dacă
    // userul a golit câmpul de expirare, nu i-l umplem la loc la fiecare tastă.
    const fresh = next.trim() ? expiryFromCertificate(firstCertificate(inspectPem(next))) : null;
    if (fresh && fresh !== suggested) onSuggestExpiry(fresh);
  };

  const handleFile = async (file: File) => {
    setFileError(null);
    try {
      if (file.size > FILE_MAX_PLAINTEXT_BYTES) {
        setFileError(
          t('vault_ui.form.file_too_large', {
            size: formatBytes(file.size),
            limit: formatBytes(FILE_MAX_PLAINTEXT_BYTES),
          }),
        );
        return;
      }
      const bytes = new Uint8Array(await file.arrayBuffer());
      let text: string | null = null;
      try {
        // Un fișier binar (DER, PKCS#12) nu are ce căuta într-o zonă de text.
        text = bytes.includes(0) ? null : new TextDecoder('utf-8', { fatal: true }).decode(bytes);
      } catch {
        text = null;
      }
      if (text === null) {
        setFileError(t('pem.file_not_text'));
        return;
      }
      apply(text);
    } catch (e) {
      setFileError(e instanceof Error ? e.message : t('vault_ui.form.read_error'));
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        {label}
        <button
          type="button"
          className="text-[10px] text-[var(--text-dim)] hover:text-brand-600 mb-1"
          onClick={() => fileRef.current?.click()}
          disabled={disabled}
        >
          {t('pem.load_file')}
        </button>
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
          }}
        />
      </div>
      <textarea
        id={inputId}
        className="input font-mono resize-y"
        rows={3}
        value={value}
        onChange={(e) => apply(e.target.value)}
        required={attr.required}
        disabled={disabled}
        placeholder={attr.default ?? undefined}
        spellCheck={false}
        aria-describedby={warning ? `${inputId}-pem` : undefined}
      />
      {warning && (
        <div
          id={`${inputId}-pem`}
          className="text-[11px] mt-1"
          style={{ color: 'var(--c-amber)' }}
          data-testid="pem-warning"
        >
          {t(PEM_WARNING_KEYS[warning])} {t('pem.not_blocking')}
        </div>
      )}
      {certificate && (
        <div
          className="text-[11px] mt-1"
          style={{ color: 'var(--text-dim)' }}
          data-testid="pem-certificate"
        >
          {certificate.subjectCn
            ? t('pem.cert_valid_until_cn', {
                cn: certificate.subjectCn,
                date: formatDate(certificate.notAfter),
              })
            : t('pem.cert_valid_until', { date: formatDate(certificate.notAfter) })}
          {onSuggestExpiry && suggested && suggested !== expires && (
            <button
              type="button"
              className="ml-2 text-brand-700 dark:text-brand-300 hover:underline"
              onClick={() => onSuggestExpiry(suggested)}
              disabled={disabled}
            >
              {t('pem.use_as_expiry')}
            </button>
          )}
        </div>
      )}
      {fileError && (
        <div className="text-xs mt-1" style={{ color: 'var(--danger)' }}>
          {fileError}
        </div>
      )}
    </div>
  );
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}
